import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Cpu, AlertTriangle, CheckCircle, Loader, HardDrive, Info, Download, PackageOpen } from 'lucide-react';
import { filesAPI } from '../utils/api';
import { parseImageWTY, getItemsByRole } from '../utils/imageWTY';
import { FelDevice } from '../utils/felProtocol';

export default function FirmwarePage() {
  const { i18n } = useTranslation();
  const isHe = i18n.language === 'he';
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [device, setDevice] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, connecting, connected, uploading, done, error
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const [assignedFirmwares, setAssignedFirmwares] = useState([]);
  const [loadingFirmwares, setLoadingFirmwares] = useState(true);
  const [usingFirmwareId, setUsingFirmwareId] = useState(null);

  const [parsedFirmware, setParsedFirmware] = useState(null);
  const [flashLog, setFlashLog] = useState([]);
  const [phase, setPhase] = useState('');
  const [felDevice, setFelDevice] = useState(null);
  const [needsRepair, setNeedsRepair] = useState(false);

  const addLog = (type, message) => {
    setFlashLog(prev => [{ type, message, time: new Date().toLocaleTimeString() }, ...prev].slice(0, 100));
  };

  useEffect(() => {
    filesAPI.list()
      .then(res => {
        const firmwares = res.data.filter(f => f.category === 'firmware');
        setAssignedFirmwares(firmwares);
      })
      .catch(() => {})
      .finally(() => setLoadingFirmwares(false));
  }, []);

  const parseFirmwareBuffer = async (buffer, fileName) => {
    setParsedFirmware(null);
    try {
      const parsed = parseImageWTY(buffer);
      const roles = getItemsByRole(parsed.items);
      setParsedFirmware({ ...parsed, roles });
      addLog('info', `Firmware parsed: ${parsed.items.length} items, ${(buffer.byteLength / 1024 / 1024).toFixed(1)} MB`);
      if (roles.fes) addLog('info', `FES: ${roles.fes.dataLen} bytes`);
      if (roles.fullimg) addLog('info', `Full image: ${(roles.fullimg.dataLen / 1024 / 1024).toFixed(1)} MB`);
    } catch (err) {
      setError(isHe ? `שגיאה בפענוח קובץ: ${err.message}` : `Firmware parse error: ${err.message}`);
      addLog('error', `Parse failed: ${err.message}`);
    }
  };

  const handleUseFirmware = async (fw) => {
    setUsingFirmwareId(fw.id);
    setError('');
    try {
      const token = localStorage.getItem('sophistiscan-token');
      const response = await fetch(`${filesAPI.downloadUrl(fw.id)}?token=${token}`);
      if (!response.ok) throw new Error(isHe ? 'הורדה נכשלה' : 'Download failed');
      const blob = await response.blob();
      const fileObj = new File([blob], fw.originalName, { type: blob.type });
      setFile(fileObj);
      setStatus(prev => prev === 'connected' ? 'connected' : 'idle');
      const buffer = await fileObj.arrayBuffer();
      await parseFirmwareBuffer(buffer, fw.originalName);
    } catch (err) {
      setError(err.message);
      addLog('error', err.message);
    } finally {
      setUsingFirmwareId(null);
    }
  };

  const handleFileSelect = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setFile(selected);
    setStatus(prev => prev === 'connected' ? 'connected' : 'idle');
    setError('');
    const buffer = await selected.arrayBuffer();
    await parseFirmwareBuffer(buffer, selected.name);
  };

  const handleConnect = async () => {
    if (!('usb' in navigator)) {
      setError(isHe ? 'WebUSB לא נתמך בדפדפן זה. השתמש בכרום.' : 'WebUSB not supported. Use Chrome.');
      return;
    }

    setStatus('connecting');
    setError('');
    setNeedsRepair(false);

    try {
      const fel = new FelDevice({
        onProgress: setProgress,
        onStatus: (s) => setPhase(s),
        onLog: (msg) => addLog('protocol', msg),
      });
      await fel.connectFEL();
      const info = await fel.felVerifyDevice();
      setFelDevice(fel);
      setDevice(info);
      setStatus('connected');
      addLog('success', 'Device probing complete — check log for protocol details');
    } catch (err) {
      if (err.name !== 'NotFoundError') {
        setError(err.message);
        addLog('error', err.message);
      }
      setStatus('idle');
    }
  };

  const handleRepair = async () => {
    setNeedsRepair(false);
    setError('');
    addLog('info', isHe ? 'מנסה חיבור מחדש במצב FES...' : 'Attempting FES re-pair...');
    await handleConnect();
  };

  const handleUpload = async () => {
    if (!felDevice || !parsedFirmware) return;

    setStatus('uploading');
    setProgress(0);
    setNeedsRepair(false);

    try {
      await felDevice.flashFirmware(parsedFirmware);
      setStatus('done');
      addLog('success', isHe ? 'עדכון הקושחה הושלם בהצלחה!' : 'Firmware update completed successfully!');
    } catch (err) {
      setError(err.message);
      setStatus('error');
      addLog('error', `Flash failed: ${err.message}`);
      // If the error suggests a FES reconnection issue, show re-pair button
      if (err.message && (err.message.includes('disconnect') || err.message.includes('FES') || err.message.includes('transfer'))) {
        setNeedsRepair(true);
        addLog('info', isHe ? 'ייתכן שנדרש חיבור מחדש. לחץ על "חבר מחדש".' : 'Re-pairing may be needed. Click "Re-pair Device".');
      }
    }
  };

  const steps = [
    {
      num: 1,
      title: isHe ? 'בחר קובץ firmware' : 'Select firmware file',
      desc: isHe ? 'קובץ .img של הגרסה החדשה' : '.img file of the new version',
    },
    {
      num: 2,
      title: isHe ? 'הכנס את הסורק למצב עדכון' : 'Put scanner in update mode',
      desc: isHe ? 'לחץ לחיצה ארוכה על ההדק וחבר USB' : 'Hold trigger + plug USB cable',
    },
    {
      num: 3,
      title: isHe ? 'חבר את הסורק' : 'Connect scanner',
      desc: isHe ? 'לחץ על כפתור "חבר סורק" ובחר את ההתקן' : 'Click "Connect" and select the device',
    },
    {
      num: 4,
      title: isHe ? 'התחל עדכון' : 'Start update',
      desc: isHe ? 'אל תנתק את הסורק עד שהעדכון יסתיים' : 'Do not disconnect until update completes',
    },
  ];

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-100 flex items-center gap-3">
          <Cpu className="w-7 h-7 text-orange-400" />
          {isHe ? 'עדכון קושחה' : 'Firmware Update'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isHe ? 'עדכון גרסת קושחה לסורק SQ400H דרך USB' : 'Update SQ400H scanner firmware via USB'}
        </p>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-6">
        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-300">
          <p className="font-semibold">{isHe ? 'אזהרה' : 'Warning'}</p>
          <p className="text-amber-400/80 mt-1">
            {isHe
              ? 'אל תנתק את הסורק במהלך העדכון. ניתוק עלול לגרום נזק לסורק.'
              : 'Do not disconnect the scanner during update. Disconnecting may permanently damage the scanner.'}
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {steps.map((step) => (
          <div key={step.num} className="card p-4 text-center">
            <div className="w-8 h-8 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center mx-auto mb-3">
              <span className="text-sm font-bold text-brand-400">{step.num}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-200 mb-1">{step.title}</h3>
            <p className="text-xs text-gray-500">{step.desc}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* File Selection */}
        <div className="card p-6">
          <h3 className="font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-blue-400" />
            {isHe ? 'קובץ קושחה' : 'Firmware File'}
          </h3>

          <input
            ref={fileInputRef}
            type="file"
            accept=".img,.bin,.fw"
            onChange={handleFileSelect}
            className="hidden"
          />

          {file ? (
            <div className="bg-black/20 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-200 font-mono">{file.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-[#2a3348] rounded-lg p-8 text-center cursor-pointer hover:border-brand-500/50 transition-colors mb-4"
            >
              <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                {isHe ? 'לחץ לבחירת קובץ .img' : 'Click to select .img file'}
              </p>
            </div>
          )}

          {/* Parsed firmware info */}
          {parsedFirmware && (
            <div className="rounded-lg p-3 border mb-4" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                {isHe ? 'קובץ תקין' : 'Valid firmware'} — {parsedFirmware.items.length} {isHe ? 'רכיבים' : 'items'}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                {parsedFirmware.roles.fes ? '✓ FES' : '✗ FES'} |
                {parsedFirmware.roles.boot0 ? ' ✓ BOOT0' : ' ✗ BOOT0'} |
                {parsedFirmware.roles.fullimg ? ` ✓ Image ${(parsedFirmware.roles.fullimg.dataLen / 1024 / 1024).toFixed(1)}MB` : ' ✗ Image'}
              </p>
            </div>
          )}

          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-secondary w-full flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {file ? (isHe ? 'החלף קובץ' : 'Change File') : (isHe ? 'בחר קובץ' : 'Select File')}
          </button>

          {/* Assigned firmwares */}
          {!loadingFirmwares && assignedFirmwares.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5">
                <PackageOpen className="w-3.5 h-3.5" />
                {isHe ? 'קושחות מוכנות להתקנה' : 'Available firmwares'}
              </p>
              <div className="space-y-2">
                {assignedFirmwares.map(fw => (
                  <div key={fw.id} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-200 truncate">
                        {fw.description || fw.originalName}
                      </p>
                      {fw.description && (
                        <p className="text-xs text-gray-600 truncate font-mono mt-0.5">{fw.originalName}</p>
                      )}
                      <p className="text-xs text-gray-600 mt-0.5">{(fw.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <a
                        href={`${filesAPI.downloadUrl(fw.id)}?token=${localStorage.getItem('sophistiscan-token')}`}
                        download={fw.originalName}
                        className="btn-secondary flex items-center gap-1 text-xs py-1 px-2"
                      >
                        <Download className="w-3 h-3" />
                        {isHe ? 'הורד' : 'Download'}
                      </a>
                      <button
                        onClick={() => handleUseFirmware(fw)}
                        disabled={usingFirmwareId === fw.id}
                        className="btn-primary flex items-center gap-1 text-xs py-1 px-2"
                      >
                        {usingFirmwareId === fw.id ? (
                          <Loader className="w-3 h-3 animate-spin" />
                        ) : (
                          <Cpu className="w-3 h-3" />
                        )}
                        {isHe ? 'השתמש' : 'Use'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Connection & Upload */}
        <div className="card p-6">
          <h3 className="font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <Cpu className="w-5 h-5 text-orange-400" />
            {isHe ? 'חיבור ועדכון' : 'Connection & Update'}
          </h3>

          {/* Status */}
          <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 ${
            status === 'connected' || status === 'done' ? 'bg-green-500/10 border border-green-500/20' :
            status === 'error' ? 'bg-red-500/10 border border-red-500/20' :
            'bg-black/20 border border-[#2a3348]'
          }`}>
            <div className={`w-3 h-3 rounded-full ${
              status === 'connected' ? 'bg-green-500 animate-pulse' :
              status === 'uploading' ? 'bg-orange-500 animate-pulse' :
              status === 'done' ? 'bg-green-500' :
              status === 'error' ? 'bg-red-500' :
              'bg-gray-600'
            }`} />
            <span className="text-sm text-gray-300">
              {status === 'idle' && (isHe ? 'ממתין לחיבור' : 'Waiting for connection')}
              {status === 'connecting' && (isHe ? 'מתחבר...' : 'Connecting...')}
              {status === 'connected' && (isHe ? 'סורק מחובר במצב עדכון' : 'Scanner connected in update mode')}
              {status === 'uploading' && (isHe ? `מעדכן... ${progress}%` : `Uploading... ${progress}%`)}
              {status === 'done' && (isHe ? 'העדכון הושלם בהצלחה!' : 'Update completed successfully!')}
              {status === 'error' && (isHe ? 'שגיאה' : 'Error')}
            </span>
          </div>

          {/* Progress bar */}
          {status === 'uploading' && (
            <div className="mb-4">
              <div className="w-full bg-gray-800 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-brand-600 to-brand-400 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {phase && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{phase}</p>}
            </div>
          )}

          {error && (
            <p className="text-sm text-red-400 mb-4">{error}</p>
          )}

          {status === 'done' && (
            <div className="flex items-center gap-2 text-green-400 mb-4">
              <CheckCircle className="w-5 h-5" />
              <p className="text-sm">{isHe ? 'נתק את הסורק והפעל מחדש' : 'Disconnect scanner and restart'}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleConnect}
              disabled={!parsedFirmware || status === 'uploading' || status === 'connected'}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {status === 'connecting' ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Cpu className="w-4 h-4" />
              )}
              {isHe ? 'חבר סורק (מצב עדכון)' : 'Connect Scanner (Update Mode)'}
            </button>

            <button
              onClick={handleUpload}
              disabled={!felDevice || !parsedFirmware || status === 'uploading' || status === 'done'}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              {status === 'uploading' ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {isHe ? 'התחל עדכון' : 'Start Update'}
            </button>

            {/* FES re-pair button */}
            {needsRepair && (
              <button
                onClick={handleRepair}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30 transition-colors"
              >
                <Cpu className="w-4 h-4" />
                {isHe ? 'חבר מחדש (מצב FES)' : 'Re-pair Device (FES Mode)'}
              </button>
            )}
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 mt-4 text-xs text-gray-600">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              {isHe
                ? 'הסורק חייב להיות במצב FEL (לחיצה ארוכה על ההדק + חיבור USB). בחר קובץ קושחה כדי להתחיל.'
                : 'Scanner must be in FEL mode (hold trigger + plug USB). Select a firmware file to begin.'}
            </p>
          </div>

          {/* Flash log */}
          {flashLog.length > 0 && (
            <div className="mt-4 rounded-lg p-3" style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                {isHe ? 'יומן עדכון' : 'Update Log'}
              </p>
              <div className="max-h-40 overflow-y-auto font-mono text-xs space-y-0.5">
                {flashLog.map((entry, i) => (
                  <div key={i} style={{ color: entry.type === 'error' ? '#ef4444' : entry.type === 'success' ? '#22c55e' : 'var(--text-muted)' }}>
                    [{entry.time}] {entry.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
